import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IViewClass, IViewPredictionsSummary } from 'models/icardio';
import MultiSelect, { IMultiSelectGroup, IMultiSelectOption } from './MultiSelect';
import { VIEW_GROUPS, matchesViewGroupMember } from './viewGroups';
import {
  FilterButton,
  FilterButtons,
  FilterContainer,
  FilterInfoMessage,
  FilterLabel,
  FilterPopover,
  FilterSection,
  FilterTitle,
} from './FilterSelector.style';

export interface IFilterValues {
  pipelines: string[];
  viewClasses: string[];
}

export const EMPTY_FILTERS: IFilterValues = { pipelines: [], viewClasses: [] };

export const formatPipelineLabel = (pipelineName: string, pipelineId: string) =>
  `${pipelineName} (${pipelineId.slice(0, 7)})`;

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  viewClasses: IViewClass[];
  onApply: (filters: IFilterValues) => void;
  initialFilters?: IFilterValues;
  viewPredictionsSummary?: IViewPredictionsSummary;
  viewPredictionsLoading: boolean;
}

const FilterSelector: FC<IProps> = ({
  isOpen,
  onClose,
  viewClasses,
  onApply,
  initialFilters,
  viewPredictionsSummary,
  viewPredictionsLoading,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>(
    initialFilters?.pipelines ?? []
  );
  const [selectedViewClasses, setSelectedViewClasses] = useState<string[]>(
    initialFilters?.viewClasses ?? []
  );

  useEffect(() => {
    setSelectedPipelines(initialFilters?.pipelines ?? []);
    setSelectedViewClasses(initialFilters?.viewClasses ?? []);
  }, [initialFilters]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleApply = useCallback(() => {
    onApply({ pipelines: selectedPipelines, viewClasses: selectedViewClasses });
    onClose();
  }, [selectedPipelines, selectedViewClasses, onApply, onClose]);

  const handleReset = useCallback(() => {
    setSelectedPipelines([]);
    setSelectedViewClasses([]);
    onApply(EMPTY_FILTERS);
    onClose();
  }, [onApply, onClose]);

  const pipelineOptions: IMultiSelectOption[] = useMemo(
    () =>
      (viewPredictionsSummary?.pipelines ?? []).map(pipeline => ({
        value: pipeline.pipelineId,
        label: formatPipelineLabel(pipeline.pipelineName, pipeline.pipelineId),
      })),
    [viewPredictionsSummary]
  );

  // Map view classes from API response to full viewClass data, sorted by window then view
  const viewClassOptions: IMultiSelectOption[] = useMemo(() => {
    const viewClassesByUuid = new Map(viewClasses?.map(vc => [vc.uuid, vc]));
    return (viewPredictionsSummary?.viewClasses ?? [])
      .map(apiViewClass => viewClassesByUuid.get(apiViewClass.uuid))
      .filter((vc): vc is IViewClass => vc !== undefined)
      .sort((a, b) =>
        a.window !== b.window ? a.window.localeCompare(b.window) : a.view.localeCompare(b.view)
      )
      .map(vc => ({ value: vc.uuid, label: `${vc.window} - ${vc.view}` }));
  }, [viewPredictionsSummary, viewClasses]);

  // Groups resolve against all view classes, not just this study's, so a group with no
  // matching predictions still filters (to an empty result)
  const viewGroupOptions: IMultiSelectGroup[] = useMemo(() => {
    const availableUuids = new Set(viewClassOptions.map(option => option.value));
    return VIEW_GROUPS.map(group => {
      const members = group.members.map(member => ({
        view: member.view,
        uuids: (viewClasses ?? [])
          .filter(vc => matchesViewGroupMember(member, vc))
          .map(vc => vc.uuid),
      }));
      const memberLines = members.map(({ view, uuids }) =>
        uuids.some(uuid => availableUuids.has(uuid)) ? view : `${view} (not in study)`
      );
      return {
        label: group.label,
        values: members.flatMap(member => member.uuids),
        title: `${group.label}:\n${memberLines.join('\n')}`,
      };
    });
  }, [viewClasses, viewClassOptions]);

  const hasNoFilterOptions =
    !viewPredictionsLoading && pipelineOptions.length === 0 && viewClassOptions.length === 0;

  return (
    <FilterPopover $isOpen={isOpen} ref={popoverRef}>
      <FilterContainer>
        <FilterTitle>Filters</FilterTitle>

        {hasNoFilterOptions ? (
          <FilterInfoMessage>
            No filter options available.
            <br />
            Filters are available when there are view predictions for this study.
          </FilterInfoMessage>
        ) : (
          <>
            <FilterSection>
              <FilterLabel>Pipeline</FilterLabel>
              <MultiSelect
                options={pipelineOptions}
                selected={selectedPipelines}
                onChange={setSelectedPipelines}
                allLabel={viewPredictionsLoading ? 'Loading...' : 'All Pipelines'}
                disabled={viewPredictionsLoading}
              />
            </FilterSection>

            <FilterSection>
              <FilterLabel>View Class</FilterLabel>
              <MultiSelect
                options={viewClassOptions}
                groups={viewGroupOptions}
                selected={selectedViewClasses}
                onChange={setSelectedViewClasses}
                allLabel={viewPredictionsLoading ? 'Loading...' : 'All Views'}
                disabled={viewPredictionsLoading}
              />
            </FilterSection>

            <FilterButtons>
              <FilterButton $variant="secondary" onClick={handleReset}>
                Reset
              </FilterButton>
              <FilterButton $variant="primary" onClick={handleApply}>
                Apply
              </FilterButton>
            </FilterButtons>
          </>
        )}
      </FilterContainer>
    </FilterPopover>
  );
};

export default FilterSelector;
